import React, { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  userPreferencesAtom,
  fetchUserPreferencesAtom,
  updateUserPreferencesAtom
} from '../../../../atoms/v3/userPreferencesAtoms';
import {
  ReminderMethodType,
  createDefaultReminderMethods,
  translateReminderMethod
} from '../../../../types/notificationMapping';
import { SettingsItem } from './SettingsItem';
import { ChannelSelector } from '../../../../components/v3/ChannelSelector';

export const NotificationPreferencesSection: React.FC = () => {
  const { t } = useTranslation(['v3/me', 'common']);
  const toast = useToast();

  const [userPreferences] = useAtom(userPreferencesAtom);
  const [, fetchPreferences] = useAtom(fetchUserPreferencesAtom);
  const [, updatePreferences] = useAtom(updateUserPreferencesAtom);

  const [selectedMethods, setSelectedMethods] = useState<ReminderMethodType[]>(
    createDefaultReminderMethods()
  );
  const [isChannelSelectorOpen, setIsChannelSelectorOpen] = useState(false);

  // 初始化时获取用户偏好
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        await fetchPreferences();
      } catch (error) {
        console.error('Failed to load user preferences:', error);
      }
    };

    if (!userPreferences) {
      loadPreferences();
    }
  }, [fetchPreferences, userPreferences]);

  // 当用户偏好变化时更新本地状态
  useEffect(() => {
    if (userPreferences?.defaultReminderMethods) {
      setSelectedMethods(userPreferences.defaultReminderMethods);
    }
  }, [userPreferences]);

  // 格式化显示的提醒方式
  const formatReminderMethods = (methods: ReminderMethodType[]): string => {
    if (methods.length === 0) return t('common:notifications.none', '未设置');
    return methods
      .map(method => translateReminderMethod(method, t))
      .join(t('common:notifications.separator', '、'));
  };

  // 处理渠道选择确认
  const handleChannelConfirm = async (newMethods: ReminderMethodType[]) => {
    try {
      await updatePreferences({
        defaultReminderMethods: newMethods
      });

      setSelectedMethods(newMethods);
      toast({
        title: t('v3/me:notifications.saveSuccess', '通知偏好已保存'),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
      toast({
        title: t('v3/me:notifications.saveFailed', '保存失败'),
        description: t('v3/me:notifications.retryLater', '请稍后重试'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  return (
    <>
      <SettingsItem
        icon={
          <svg className="w-5 h-5 text-ios-blue dark:text-ios-dark-blue" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        }
        title={t('v3/me:notifications.defaultChannels', '默认提醒渠道')}
        description={t('v3/me:notifications.defaultChannelsDescription', '设置新建提醒和日报的默认通知方式')}
        rightElement={
          <span className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray">
            {formatReminderMethods(selectedMethods)}
          </span>
        }
        onClick={() => setIsChannelSelectorOpen(true)}
        iconBgColor="bg-ios-blue/10 dark:bg-ios-dark-blue/20"
      />

      {/* 推送渠道选择器 */}
      <ChannelSelector
        isOpen={isChannelSelectorOpen}
        onClose={() => setIsChannelSelectorOpen(false)}
        selectedChannels={selectedMethods}
        onConfirm={handleChannelConfirm}
        title={t('v3/me:notifications.defaultChannels', '默认提醒渠道')}
        showUserPreferenceHint={false} // 这里不需要显示偏好提示，因为就是在设置偏好
      />
    </>
  );
};
